import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';

const LocationAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "e.g. Tokyo, Japan or New York, USA",
  className = "",
  disabled = false 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedIndex, setSelectedIndex] = useState(-1); // For keyboard navigation
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const suggestionRefs = useRef([]); // For scrolling selected items into view
  const cacheRef = useRef(new Map()); // Cache for API results
  const abortControllerRef = useRef(null); // For cancelling requests

  // Optimized search function with caching and request cancellation
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      const trimmedQuery = query.trim().toLowerCase();
      
      if (!trimmedQuery || trimmedQuery.length < 2) {
        setSuggestions([]);
        suggestionRefs.current = [];
        setIsLoading(false);
        return;
      }

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Check cache first
      if (cacheRef.current.has(trimmedQuery)) {
        setSuggestions(cacheRef.current.get(trimmedQuery));
        suggestionRefs.current = [];
        setIsLoading(false);
        return;
      }

      // Keep a local reference so a stale request never touches the latest one's controller
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setIsLoading(true);

        // Try multiple APIs for best performance and reliability
        let response;
        let apiUsed = 'photon';

        try {
          // Primary: Photon API (fastest free option)
          response = await fetch(
            `https://photon.komoot.io/api/?q=${encodeURIComponent(trimmedQuery)}&limit=6`,
            {
              signal: controller.signal,
              headers: { 'Accept': 'application/json' }
            }
          );
          if (!response.ok) throw new Error(`Photon responded ${response.status}`);
        } catch (photonError) {
          if (photonError.name === 'AbortError') throw photonError;
          // Fallback: Nominatim if Photon fails
          apiUsed = 'nominatim';
          response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&dedupe=1&q=${encodeURIComponent(trimmedQuery)}`,
            {
              signal: controller.signal,
              headers: { 'Accept': 'application/json' }
            }
          );
        }

        if (response.ok && !controller.signal.aborted) {
          const data = await response.json();
          let formattedSuggestions = [];
          
          if (apiUsed === 'photon' && data.features) {
            // Process Photon API response (GeoJSON format)
            formattedSuggestions = data.features.map(item => ({
              id: item.properties?.osm_id || `${item.geometry.coordinates[1]}-${item.geometry.coordinates[0]}`,
              display_name: item.properties?.name || '',
              formatted: formatPhotonLocation(item),
              lat: item.geometry.coordinates[1],
              lon: item.geometry.coordinates[0],
              type: item.properties?.osm_key || 'location',
              country: item.properties?.country || '',
              state: item.properties?.state || '',
              city: item.properties?.city || item.properties?.town || ''
            }));
          } else if (apiUsed === 'nominatim' && Array.isArray(data)) {
            // Process Nominatim API response (array format)
            formattedSuggestions = data.map(item => ({
              id: item.place_id,
              display_name: item.display_name,
              formatted: formatLocation(item),
              lat: item.lat,
              lon: item.lon,
              type: item.type,
              country: item.address?.country || '',
              state: item.address?.state || '',
              city: item.address?.city || item.address?.town || item.address?.village || ''
            }));
          }
          
          // Cache the results
          cacheRef.current.set(trimmedQuery, formattedSuggestions);
          
          // Limit cache size to prevent memory issues
          if (cacheRef.current.size > 100) {
            const firstKey = cacheRef.current.keys().next().value;
            cacheRef.current.delete(firstKey);
          }
          
          setSuggestions(formattedSuggestions);
          // Reset suggestion refs for new suggestions
          suggestionRefs.current = [];
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          // Silently handle errors - don't log to console in production
          setSuggestions([]);
          suggestionRefs.current = [];
        }
      } finally {
        // Only the latest request may clear loading state and the controller ref
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
          abortControllerRef.current = null;
        }
      }
    }, 100), // Reduced to 100ms for Photon's faster response times
    []
  );

  // Format location for Photon API response
  const formatPhotonLocation = (item) => {
    if (!item?.properties) return item?.properties?.name || 'Unknown location';
    
    const props = item.properties;
    const parts = [];
    
    // Add name/city
    if (props.name) parts.push(props.name);
    else if (props.city) parts.push(props.city);
    else if (props.town) parts.push(props.town);
    
    // Add state if different from name
    if (props.state && props.state !== props.name) {
      parts.push(props.state);
    }
    
    // Add country
    if (props.country) parts.push(props.country);
    
    return parts.length > 0 ? parts.join(', ') : props.name || 'Unknown location';
  };

  // Legacy format function for Nominatim (kept for fallback)
  const formatLocation = (item) => {
    const address = item.address || {};
    const parts = [];
    
    // Add city/town
    if (address.city) parts.push(address.city);
    else if (address.town) parts.push(address.town);
    else if (address.village) parts.push(address.village);
    
    // Add state/region
    if (address.state) parts.push(address.state);
    
    // Add country
    if (address.country) parts.push(address.country);
    
    return parts.length > 0 ? parts.join(', ') : item.display_name;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setSelectedIndex(-1); // Reset selection
    
    if (newValue.trim().length >= 2) {
      // Show loader during debounce window so "No locations found" doesn't flash
      if (!cacheRef.current.has(newValue.trim().toLowerCase())) setIsLoading(true);
      debouncedSearch(newValue);
    } else {
      setSuggestions([]);
      suggestionRefs.current = [];
      setIsLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      case 'Tab':
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          // Allow normal tab navigation if no item is selected
          setIsOpen(false);
          setSelectedIndex(-1);
        }
        break;
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setInputValue(suggestion.formatted);
    setIsOpen(false);
    setSuggestions([]);
    suggestionRefs.current = [];
    setSelectedIndex(-1);
    onChange(suggestion.formatted);
  };

  // Handle clear
  const handleClear = () => {
    setInputValue('');
    setIsOpen(false);
    setSuggestions([]);
    suggestionRefs.current = [];
    setSelectedIndex(-1);
    onChange('');
    inputRef.current?.focus();
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll the selected suggestion into view
  useEffect(() => {
    const index = selectedIndex;
    const el = suggestionRefs.current[index];

    if (el && isOpen) {
      el.scrollIntoView({
        block: "nearest", // Only scroll if item is out of view
        behavior: "smooth", // Smooth scroll
      });
    }
  }, [selectedIndex, isOpen]);

  // Sync with external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Input Field */}
      <div className="relative z-0">
        <input
          ref={inputRef}
          type="text"
          className={`w-full h-12 px-6 pr-10 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-colors duration-200 ${className}`}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={isOpen ? "location-suggestions" : undefined}
          aria-activedescendant={
            selectedIndex >= 0 ? `location-suggestion-${selectedIndex}` : undefined
          }
          aria-expanded={isOpen}
          role="combobox"
        />
        
        {/* Loading/Clear Icon */}
        <div className="absolute right-4 top-[58%] transform -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : inputValue ? (
            <button
              onClick={handleClear}
              className="text-gray-400 dark:hover:text-white hover:text-gray-300 transition-colors"
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul 
          id="location-suggestions"
          role="listbox"
          className="absolute z-[9999] w-full mt-1 dark:bg-gray-800 bg-bgPrimary border border-[#5A4479] rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              id={`location-suggestion-${index}`}
              ref={(el) => (suggestionRefs.current[index] = el)}
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSuggestionSelect(suggestion)}
              onMouseDown={(e) => e.preventDefault()} // Prevent input blur
              className={`w-full px-4 py-2 text-left dark:text-[#CCCCCC] text-textSecondary transition-colors cursor-pointer ${
                index === selectedIndex 
                  ? 'dark:bg-blue-600/50 bg-gray-200  dark:border-blue-500' 
                  : 'dark:hover:bg-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="dark:text-[#CCCCCC] text-textSecondary text-sm">
                    {suggestion.formatted}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* No results message */}
      {isOpen && !isLoading && inputValue.length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-[9999] w-full mt-1 dark:bg-gray-800 bg-bgPrimary border border-[#5A4479] rounded-lg shadow-2xl">
          <div className="px-4 py-2 dark:text-gray-400 text-textSecondary text-sm text-center">
            No locations found for "{inputValue}"
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default LocationAutocomplete;